import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Products } from 'src/@core/utils/getProductLevel';
import { getFile as getAttachmentFile, getTrackingAttachmentFile } from 'src/services/attachment';
import { AttachmentDto, GetTrackingStatusImageByidFileQueryParams, TrackingAttachmentDto } from 'src/models/attachment';

export interface LoadedDocumentFile {
  document: AttachmentDto | TrackingAttachmentDto;
  file: File;
}

interface UseDocumentViewerFilesParams {
  documents: (AttachmentDto | TrackingAttachmentDto)[];
  useOverride: boolean;
  product: Products | 0;
  onPartialFailure?: () => void;
  maxConcurrency?: number;
  enabled?: boolean;
}

const DEFAULT_MAX_CONCURRENCY = 4;

const buildDocumentCacheKey = (document: AttachmentDto | TrackingAttachmentDto): string => {
  const trackingHash = (document as TrackingAttachmentDto).hash ?? '';

  return [document.id ?? '', document.name ?? '', document.fileType ?? '', trackingHash].join('|');
};

const runWithConcurrency = async <T>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<T[]> => {
  if (tasks.length === 0) return [];

  const concurrency = Math.max(1, limit);
  const results = new Array<T>(tasks.length);
  let cursor = 0;

  const worker = async () => {
    while (cursor < tasks.length) {
      const current = cursor;
      cursor += 1;
      results[current] = await tasks[current]();
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker()));

  return results;
};

export const useDocumentViewerFiles = ({
  documents,
  useOverride,
  product,
  onPartialFailure,
  maxConcurrency = DEFAULT_MAX_CONCURRENCY,
  enabled = true
}: UseDocumentViewerFilesParams) => {
  const [files, setFiles] = useState<LoadedDocumentFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, File>>(new Map());
  const inFlightRef = useRef<Map<string, Promise<File | null>>>(new Map());

  const documentsForProduct = useMemo(() => {
    return product === Products.MyPortal ? documents.filter(doc => !doc.isHidden) : documents;
  }, [documents, product]);

  const getFileBlob = useCallback(
    async (doc: AttachmentDto | TrackingAttachmentDto) => {
      if (useOverride) {
        const params: GetTrackingStatusImageByidFileQueryParams = { Hash: (doc as TrackingAttachmentDto).hash ?? '' };
        const data = await getTrackingAttachmentFile(doc.id ?? '', params);

        return data.data;
      }

      const data = await getAttachmentFile(doc.id ?? '');

      return data.data;
    },
    [useOverride]
  );

  const getOrLoadFile = useCallback(
    async (doc: AttachmentDto | TrackingAttachmentDto): Promise<File | null> => {
      const cacheKey = buildDocumentCacheKey(doc);
      const cached = cacheRef.current.get(cacheKey);
      if (cached) return cached;

      const inFlight = inFlightRef.current.get(cacheKey);
      if (inFlight) return inFlight;

      const request = (async () => {
        try {
          const fileBlob = await getFileBlob(doc);
          if (!fileBlob) return null;

          const name = doc.name ?? doc.id ?? 'document';
          const type = doc.fileType ?? 'application/octet-stream';
          const file = new File([fileBlob], name, { type });
          cacheRef.current.set(cacheKey, file);

          return file;
        } catch {
          return null;
        } finally {
          inFlightRef.current.delete(cacheKey);
        }
      })();

      inFlightRef.current.set(cacheKey, request);

      return request;
    },
    [getFileBlob]
  );

  useEffect(() => {
    if (!enabled) return;

    if (documentsForProduct.length === 0) {
      setFiles([]);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);
    let isActive = true;

    const fetchFiles = async () => {
      const filePromises = documentsForProduct.map(
        doc => async (): Promise<LoadedDocumentFile | null> => {
          const file = await getOrLoadFile(doc);
          if (!file) return null;

          return { document: doc, file };
        }
      );

      const fetchedFiles = await runWithConcurrency(filePromises, maxConcurrency);
      const validFiles = fetchedFiles.filter((file): file is LoadedDocumentFile => file !== null);

      if (!isActive) return;

      if (validFiles.length < documentsForProduct.length) {
        onPartialFailure?.();
      }

      setFiles(validFiles);
      setIsLoading(false);
    };

    fetchFiles();

    return () => {
      isActive = false;
    };
  }, [documentsForProduct, enabled, getOrLoadFile, maxConcurrency, onPartialFailure]);

  return { files, documentsForProduct, isLoading };
};
